import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AlunniSendBar from "@/components/AlunniSendBar";
import useAlunniSelection from "@/lib/useAlunniSelection";
import { useAnnoScolastico } from "@/lib/annoScolastico";

const ANNO_DEFAULT = new Date().getFullYear() - (new Date().getMonth() >= 7 ? 0 : 1);

export default function ScuolaAgevolazioniReportPage({ initialTab = "report" }) {
  const { anno: annoGlobal, setAnno: setAnnoGlobal } = useAnnoScolastico();
  // 🆕 Inizializza dall'anno globale della gestione, altrimenti default
  const [anno, setAnno] = useState(annoGlobal != null ? annoGlobal : ANNO_DEFAULT);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filtroSettore, setFiltroSettore] = useState("");
  const [soloAttive, setSoloAttive] = useState(false);
  const [soloConAgev, setSoloConAgev] = useState(true);  // default: mostra solo alunni con agevolazione netta > 0
  // 🆕 Feb 2026: Ordinamento report — "cognome_nome" (default) oppure "settore_classe"
  const [sortBy, setSortBy] = useState("cognome_nome");
  // 🆕 Tab attiva dichiarata QUI (prima dei useEffect che la usano)
  const [tab, setTab] = useState(initialTab || "report");
  const sel = useAlunniSelection();

  // 🆕 Sincronizza con l'anno globale della gestione (header): se cambia da altre pagine, aggiorna qui.
  useEffect(() => {
    if (annoGlobal != null && annoGlobal !== anno) {
      setAnno(annoGlobal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoGlobal]);
  // Quando cambia l'anno locale dal <input> nella pagina, aggiorna anche il globale
  useEffect(() => {
    if (anno != null && anno !== annoGlobal) setAnnoGlobal(anno);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anno]);

  const carica = async (a, includeAnyYear = false) => {
    setLoading(true);
    try {
      const r = await api.get("/agevolazioni/report", { params: { anno_scol: a, attive_only: soloAttive, include_any_year: includeAnyYear } });
      setRows(r.data?.data || []);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  // 🆕 Feb 2026: quando siamo su tab "comparatore" carichiamo tutti gli alunni con
  // agevolazione in QUALSIASI anno (con placeholder per anno_scol se mancante).
  useEffect(() => {
    carica(anno, tab === "comparatore");
    /* eslint-disable-next-line */
  }, [anno, soloAttive, tab]);

  const settori = useMemo(() => Array.from(new Set(rows.map(r => r.settore).filter(Boolean))).sort(), [rows]);

  const cambiaStato = async (cod, newStato) => {
    // 🆕 Feb 2026: usa direttamente lo stato reale del backend (nessun rename BOZZA→CONFERMATA)
    try {
      await api.post(`/agevolazioni/${cod}/${anno}/stato`, { stato: newStato });
      setRows(rs => rs.map(r => r.cod === cod ? { ...r, stato: newStato } : r));
    } catch (e) {
      alert("⚠ Errore cambio stato: " + (e.response?.data?.detail || e.message));
    }
  };

  // 🆕 Feb 2026: Lista completa stati agevolazione + colori
  const STATI_AGEV = ["BOZZA", "APPROVATA", "FIRMATA", "RATE", "SC_FRATELLI", "APPUNTAMENTO", "NO", "USCITO"];
  const statoColor = (s) => {
    const map = {
      BOZZA:        { bg: "#fff3e0", fg: "#ef6c00" },
      APPROVATA:    { bg: "#e8f5e9", fg: "#1b5e20" },
      FIRMATA:      { bg: "#c8e6c9", fg: "#1b5e20" },
      RATE:         { bg: "#e3f2fd", fg: "#0d47a1" },
      SC_FRATELLI:  { bg: "#f3e5f5", fg: "#4a148c" },
      APPUNTAMENTO: { bg: "#fff9c4", fg: "#f57f17" },
      NO:           { bg: "#ffebee", fg: "#b71c1c" },
      USCITO:       { bg: "#eeeeee", fg: "#616161" },
      NESSUNA:      { bg: "#fafafa", fg: "#999" },
    };
    return map[s] || map.BOZZA;
  };

  // 🆕 Feb 2026: stati "workflow" da mostrare SEMPRE nel report, anche se
  //   agev_netta = 0 (es. RATE = piano rate senza sconto sul totale ma va monitorato).
  //   BOZZA/NO/USCITO/NESSUNA restano soggetti al filtro "Solo con agev netta > 0".
  const STATI_SEMPRE_VISIBILI = new Set(["APPROVATA", "FIRMATA", "RATE", "SC_FRATELLI", "APPUNTAMENTO"]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const base = rows.filter(r => {
      if (filtroSettore && r.settore !== filtroSettore) return false;
      // Se lo stato è "workflow" (APPROVATA/FIRMATA/RATE/SC_FRATELLI/APPUNTAMENTO),
      // ignora il filtro "solo con agev" — dev'essere sempre visibile.
      const isWorkflowStato = STATI_SEMPRE_VISIBILI.has(r.stato);
      if (!isWorkflowStato && soloConAgev && (Number(r.agev_netta) || 0) <= 0) return false;
      if (!q) return true;
      return (
        String(r.cod).includes(q) ||
        (r.cognome || "").toLowerCase().includes(q) ||
        (r.nome || "").toLowerCase().includes(q) ||
        (r.classe || "").toLowerCase().includes(q)
      );
    });
    // 🆕 Feb 2026: Ordinamento
    const settOrder = { "INFANZIA": 1, "PRIMARIA": 2, "SECONDARIA": 3 };
    const classeKey = (c) => {
      const s = String(c || "").toLowerCase();
      if (s === "i") return 0;
      const m = s.match(/(\d+)/);
      return m ? parseInt(m[1]) : 99;
    };
    return [...base].sort((a, b) => {
      if (sortBy === "netta_desc") {
        const na = Number(a.agev_netta) || 0;
        const nb = Number(b.agev_netta) || 0;
        if (na !== nb) return nb - na;
      } else if (sortBy === "netta_asc") {
        const na = Number(a.agev_netta) || 0;
        const nb = Number(b.agev_netta) || 0;
        if (na !== nb) return na - nb;
      } else if (sortBy === "settore_classe") {
        const sa = settOrder[a.settore] || 99;
        const sb = settOrder[b.settore] || 99;
        if (sa !== sb) return sa - sb;
        const ka = classeKey(a.classe);
        const kb = classeKey(b.classe);
        if (ka !== kb) return ka - kb;
      }
      const ca = (a.cognome || "").toLowerCase();
      const cb = (b.cognome || "").toLowerCase();
      if (ca !== cb) return ca < cb ? -1 : 1;
      const na = (a.nome || "").toLowerCase();
      const nb = (b.nome || "").toLowerCase();
      if (na !== nb) return na < nb ? -1 : 1;
      return 0;
    });
  }, [rows, search, filtroSettore, soloConAgev, sortBy]);

  // 🆕 Per il Comparatore: stessi filtri ma ordinati SOLO per Cognome + Nome
  const filteredComp = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ca = (a.cognome || "").toLowerCase();
      const cb = (b.cognome || "").toLowerCase();
      if (ca !== cb) return ca < cb ? -1 : 1;
      const na = (a.nome || "").toLowerCase();
      const nb = (b.nome || "").toLowerCase();
      if (na !== nb) return na < nb ? -1 : 1;
      return 0;
    });
  }, [filtered]);

  // Totali
  const tot = useMemo(() => filtered.reduce((acc, r) => {
    acc.isc_listino += r.isc_listino;
    acc.isc_accord += r.isc_accord;
    acc.isc_agev += r.isc_agev;
    acc.tot_rette_listino += r.tot_rette_listino;
    acc.tot_rette_accord += r.tot_rette_accord;
    acc.agev_rette += r.agev_rette;
    acc.mensa_gg += Number(r.mensa_gg) || 0;
    acc.agev_mensa += r.agev_mensa;
    acc.agev_totale += r.agev_totale;
    acc.dote_scuola += r.dote_scuola;
    acc.agev_netta += r.agev_netta;
    return acc;
  }, { isc_listino: 0, isc_accord: 0, isc_agev: 0, tot_rette_listino: 0, tot_rette_accord: 0, agev_rette: 0, mensa_gg: 0, agev_mensa: 0, agev_totale: 0, dote_scuola: 0, agev_netta: 0 }), [filtered]);

  // 🆕 Feb 2026: valori Mensa "comuni" (settimane, listino €/pasto) — mostrati una sola volta
  // sopra la tabella se sono uguali per tutti gli alunni filtrati.
  const mensaComune = useMemo(() => {
    if (filtered.length === 0) return { settimane: null, listino: null, allEqualSett: false, allEqualList: false };
    const settSet = new Set(filtered.map(r => Number(r.mensa_settimane) || 0));
    const listSet = new Set(filtered.map(r => Number(r.mensa_listino) || 0));
    return {
      settimane: filtered[0].mensa_settimane || 0,
      listino: filtered[0].mensa_listino || 0,
      allEqualSett: settSet.size === 1,
      allEqualList: listSet.size === 1,
    };
  }, [filtered]);

  const exportCsv = () => {
    const headers = ["Cod","Cognome","Nome","Settore","Classe","Stato","ISEE","% Contrib","Isc Listino","Isc Accord","Isc Agev","Nr Rette List","Val Retta List","Tot Rette List","Nr Rette Accord","Val Retta Accord","Tot Rette Accord","Agev Rette","Mensa sett.","Mensa gg","Mensa Listino","Mensa Agev","Agev Mensa","Agev Totale","Dote Scuola","Agev Netta"];
    const lines = [headers.join(";")];
    filtered.forEach(r => {
      const statoUI = r.stato || "BOZZA";
      // Percentuale contribuzione = 34 + ISEE × (100−34) / (32000−0), clamp [34%, 100%]
      let pctContrib = "";
      if (r.isee != null && r.isee !== "") {
        const n = Number(r.isee);
        if (Number.isFinite(n)) {
          let pct = 34 + n * (100 - 34) / 32000;
          if (pct < 34) pct = 34;
          if (pct > 100) pct = 100;
          pctContrib = pct.toFixed(2);
        }
      }
      lines.push([r.cod, r.cognome, r.nome, r.settore, r.classe, statoUI, r.isee ?? "", pctContrib, r.isc_listino, r.isc_accord, r.isc_agev, r.nr_rette_listino, r.val_retta_listino, r.tot_rette_listino, r.nr_rette_accord, r.val_retta_accord, r.tot_rette_accord, r.agev_rette, r.mensa_settimane, r.mensa_gg, r.mensa_listino, r.mensa_agevolato, r.agev_mensa, r.agev_totale, r.dote_scuola, r.agev_netta].map(v => String(v ?? "").replaceAll(";", ",")).join(";"));
    });
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report_agevolazioni_${anno}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // 🆕 Tab attiva dichiarata all'inizio della funzione (vedi sopra)
  // Prop `initialTab` permette di montare la pagina direttamente sul Comparatore
  // (usato dal menu "⚖ Comparatore" in alto).

  // 🆕 Helper % contribuzione da ISEE (34 + ISEE × 66 / 32000, clamp [34,100])
  const calcPctContrib = (iseeVal) => {
    if (iseeVal == null || iseeVal === "") return null;
    const n = Number(iseeVal);
    if (!Number.isFinite(n)) return null;
    let pct = 34 + n * (100 - 34) / 32000;
    if (pct < 34) pct = 34;
    if (pct > 100) pct = 100;
    return pct;
  };

  // 🆕 Totali colonne per Comparatore
  const totComp = useMemo(() => {
    const t = {
      isee: 0, isee_count: 0,
      isc_listino: 0, isc_agev: 0, isc_pct: 0,
      retta_list_tot: 0, retta_agev_tot: 0, retta_pct_tot: 0,
      dote: 0,
    };
    for (const r of filtered) {
      const iscL = Number(r.isc_listino) || 0;
      const iscA = Number(r.isc_accord) || 0;
      const valRl = Number(r.val_retta_listino) || 0;
      const nrRl = Number(r.nr_rette_listino) || 0;
      const nrRa = Number(r.nr_rette_accord) || 0;
      const dote = Number(r.dote_scuola) || 0;
      const pct = calcPctContrib(r.isee);
      const iscPct = pct != null ? (iscL * pct / 100) : iscL;
      // 🆕 Feb 2026 formula: ((nrRl × valRl × pct/100) + dote) / nrRa
      const numerRettaPct = pct != null ? ((nrRl * valRl * pct / 100) + dote) : (nrRl * valRl + dote);
      const rettaPctPerRata = nrRa > 0 ? (numerRettaPct / nrRa) : 0;
      if (r.isee != null && r.isee !== "" && Number.isFinite(Number(r.isee))) {
        t.isee += Number(r.isee);
        t.isee_count += 1;
      }
      t.isc_listino += iscL;
      t.isc_agev += iscA;
      t.isc_pct += iscPct;
      t.retta_list_tot += Number(r.tot_rette_listino) || 0;
      t.retta_agev_tot += Number(r.tot_rette_accord) || 0;
      t.retta_pct_tot += rettaPctPerRata;
      t.dote += dote;
    }
    // Media ISEE (solo sui valorizzati)
    t.isee_media = t.isee_count > 0 ? (t.isee / t.isee_count) : 0;
    return t;
  }, [filtered]);

  return (
    <div>
      {/* 🆕 Feb 2026: CSS di stampa A3 orizzontale — attivato dal pulsante "Stampa A3" */}
      <style>{`
        @media print {
          @page { size: A3 landscape; margin: 8mm; }
          body { background: white !important; }
          /* Nascondi tutto tranne la tabella printable */
          body * { visibility: hidden !important; }
          #agev-rep-printable, #agev-rep-printable * { visibility: visible !important; }
          #agev-rep-printable {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            width: 100% !important; margin: 0 !important;
            max-height: none !important; overflow: visible !important;
            border: none !important;
          }
          .agev-rep-table { font-size: 8pt !important; page-break-inside: auto !important; }
          .agev-rep-table thead { display: table-header-group; }
          .agev-rep-table tfoot { display: table-footer-group; }
          .agev-rep-table tr { page-break-inside: avoid; }
          .agev-rep-table select { border: none !important; background: transparent !important; -webkit-appearance: none !important; appearance: none !important; }
          .agev-rep-mensa-banner { page-break-after: avoid; }
        }
      `}</style>
      <div className="sw-page-title">
        <h1>🎓 Report Agevolazioni — Schede per anno</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => window.print()} disabled={filtered.length === 0 || tab !== "report"} data-testid="agev-rep-print"
            style={{ padding: "6px 14px", background: "#4527a0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: (filtered.length === 0 || tab !== "report") ? "not-allowed" : "pointer" }}
            title="Stampa in orizzontale A3">
            🖨 Stampa A3 (orizzontale)
          </button>
          <button onClick={exportCsv} disabled={filtered.length === 0 || tab !== "report"} data-testid="agev-rep-csv"
            style={{ padding: "6px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: (filtered.length === 0 || tab !== "report") ? "not-allowed" : "pointer" }}>
            ⬇ Esporta CSV
          </button>
        </div>
      </div>

      {/* 🗑 Tab switcher Report/Comparatore rimosso (feb 2026 richiesta utente):
          il Comparatore ora è una voce indipendente del menu principale della gestione
          (⚖ Comparatore in navbar). Il flag `tab` resta per usare `initialTab` da menu. */}

      <div style={{ margin: "8px 20px", padding: 10, background: "white", border: "1px solid #ccc", display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Anno scolastico</div>
          <input type="number" value={anno} onChange={e => setAnno(parseInt(e.target.value) || ANNO_DEFAULT)}
            data-testid="agev-rep-anno"
            style={{ width: 90, padding: "4px 8px", border: "1px solid #888", fontSize: 13, fontWeight: 700 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Settore</div>
          <select value={filtroSettore} onChange={e => setFiltroSettore(e.target.value)}
            data-testid="agev-rep-settore"
            style={{ padding: "4px 8px", border: "1px solid #888", fontSize: 12 }}>
            <option value="">Tutti</option>
            {settori.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Ordinamento</div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            data-testid="agev-rep-sort"
            style={{ padding: "4px 8px", border: "1px solid #888", fontSize: 12, fontWeight: 700 }}>
            <option value="cognome_nome">Cognome + Nome</option>
            <option value="settore_classe">Settore + Classe (poi Cognome)</option>
            <option value="netta_desc">💰 Agev. Netta (↓ decrescente)</option>
            <option value="netta_asc">💰 Agev. Netta (↑ crescente)</option>
          </select>
        </div>
        <div style={{ flex: "1 1 250px" }}>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Cerca (cod/cognome/nome/classe)</div>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            data-testid="agev-rep-search"
            placeholder="Digita per filtrare..."
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={soloAttive} onChange={e => setSoloAttive(e.target.checked)} data-testid="agev-rep-only-attive" />
          Solo attive
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", fontWeight: 700, color: "#0d47a1" }}>
          <input type="checkbox" checked={soloConAgev} onChange={e => setSoloConAgev(e.target.checked)} data-testid="agev-rep-only-net-positive" />
          Solo con agevolazione netta &gt; 0
        </label>
        <button onClick={() => carica(anno)} disabled={loading}
          style={{ padding: "6px 14px", background: "#00695c", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{loading ? "..." : "↻ Aggiorna"}</button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#444" }}>
          <b>{filtered.length}</b> alunni
        </div>
      </div>

      <div style={{ margin: "0 20px 0" }}>
        <AlunniSendBar selected={sel.selected} onClear={sel.clear}
          label={`Agevolazioni ${anno}/${String((anno||0)+1).slice(-2)}`} anno={anno} />
      </div>

      {tab === "report" && (
      <div id="agev-rep-printable" style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", maxHeight: "75vh", overflow: "auto" }}>
        {/* 🆕 Feb 2026: banner Mensa (valori comuni) */}
        {(mensaComune.allEqualSett || mensaComune.allEqualList) && (
          <div className="agev-rep-mensa-banner" style={{
            padding: "6px 10px",
            background: "#e8eaf6",
            borderBottom: "1px solid #c5cae9",
            fontSize: 11,
            color: "#1a237e",
            fontWeight: 600,
            display: "flex",
            gap: 18,
            alignItems: "center",
          }}>
            <span style={{ fontSize: 12 }}>🍽 <b>Mensa (valori comuni):</b></span>
            {mensaComune.allEqualSett && (
              <span><b>Settimane:</b> {mensaComune.settimane}</span>
            )}
            {mensaComune.allEqualList && (
              <span><b>Costo pasto listino:</b> € {fmt(mensaComune.listino)}</span>
            )}
            <span style={{ marginLeft: "auto", fontSize: 10, opacity: 0.7 }}>
              (colonne &quot;Sett.&quot; e &quot;MensaL&quot; nascoste — stesso valore per tutti gli alunni)
            </span>
          </div>
        )}
        <table className="sw-table sw-table-compact agev-rep-table" style={{ fontSize: 9, width: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ ...th, width: 28, textAlign: "center" }}>
                <input type="checkbox" data-testid="agev-rep-selall"
                  title="Seleziona/Deseleziona tutti i visibili"
                  checked={filtered.length > 0 && filtered.every(r => sel.isSelected(r.cod))}
                  onChange={(e) => e.target.checked ? sel.selectAll(filtered.map(r => r.cod)) : sel.deselectAll(filtered.map(r => r.cod))} />
              </th>
              <th style={th}>Cod</th>
              <th style={th}>Cognome</th>
              <th style={th}>Nome</th>
              <th style={th}>Settore</th>
              <th style={th}>Classe</th>
              <th style={th}>Stato</th>
              <th style={th}>ISEE</th>
              <th style={{ ...th, background: "#e1f5fe", color: "#01579b" }} title="Percentuale contribuzione = 34 + (ISEE − 0) × (100 − 34) / (32.000 − 0), clamp [34%, 100%]">% Contrib.</th>
              <th style={th}>Isc.Listino</th>
              <th style={th}>Isc.Accord.</th>
              <th style={thHi}>Isc.Agev.</th>
              <th style={th}>NrR.L</th>
              <th style={th}>ValR.L</th>
              <th style={th}>TotR.L</th>
              <th style={th}>NrR.A</th>
              <th style={th}>ValR.A</th>
              <th style={th}>TotR.A</th>
              <th style={thHi}>Agev Retta</th>
              <th style={{ ...th, background: "#b2ebf2", color: "#006064" }} title="Giorni mensa">GG</th>
              <th style={th}>MensaA</th>
              <th style={thHi}>Agev Mensa</th>
              <th style={thHi}>Agev Totale</th>
              <th style={th}>Dote</th>
              <th style={thHiStrong}>Agev Netta</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={25} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                {loading ? "Caricamento..." : "Nessun alunno con scheda agevolazione attiva per l'anno selezionato."}
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={r.cod} data-testid={`agev-rep-row-${i}`}
                  style={{ background: sel.isSelected(r.cod) ? "#f3e5f5" : (r.attiva ? (i % 2 ? "#fafafa" : "white") : "#f5f5f5"),
                           opacity: r.attiva ? 1 : 0.6 }}>
                <td style={{ textAlign: "center" }}>
                  <input type="checkbox" data-testid={`agev-rep-sel-${r.cod}`}
                    checked={sel.isSelected(r.cod)} onChange={() => sel.toggle(r.cod)} />
                </td>
                <td style={{ textAlign: "center", fontFamily: "monospace", fontSize: 9 }}>{r.cod}</td>
                <td style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.cognome}</td>
                <td style={{ fontSize: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nome}</td>
                <td style={{ fontSize: 8, fontWeight: 700 }}>{r.settore}</td>
                <td style={{ textAlign: "center", fontWeight: 700, fontSize: 9 }}>{r.classe || "—"}</td>
                <td style={{ textAlign: "center", fontSize: 8 }}>
                  {(() => {
                    const st = r.stato || "BOZZA";
                    const col = statoColor(st);
                    return (
                      <select value={st}
                        onChange={(e) => cambiaStato(r.cod, e.target.value)}
                        data-testid={`agev-rep-stato-${r.cod}`}
                        title="Cambia stato scheda agevolazione"
                        style={{
                          fontWeight: 700,
                          fontSize: 9,
                          padding: "2px 4px",
                          border: "1px solid #ccc",
                          borderRadius: 3,
                          background: col.bg,
                          color: col.fg,
                          cursor: "pointer",
                        }}>
                        {st === "NESSUNA" && <option value="NESSUNA">NESSUNA</option>}
                        {STATI_AGEV.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    );
                  })()}
                </td>
                <td style={{
                  ...tdN,
                  fontWeight: 700,
                  color: r.isee_mancante ? "#b71c1c" : (r.isee != null ? "#0d47a1" : "#999"),
                  background: r.isee_mancante ? "#ffcdd2" : undefined,
                }}
                  title={r.isee_mancante ? "ISEE MANCANTE — flag attivo sulla scheda agevolazione" : undefined}>
                  {r.isee_mancante ? "" : (r.isee != null ? fmt(r.isee) : "—")}
                </td>
                {/* 🆕 % Contribuzione = 34 + ISEE × (100−34) / (32000−0), clamp [34%, 100%] */}
                {(() => {
                  const ISEE_INI = 0, ISEE_FIN = 32000, PCT_MIN = 34, PCT_MAX = 100;
                  let label = "—", color = "#999", bg = "#fafafa";
                  if (r.isee != null && r.isee !== "") {
                    const n = Number(r.isee);
                    if (Number.isFinite(n)) {
                      let pct = PCT_MIN + (n - ISEE_INI) * (PCT_MAX - PCT_MIN) / (ISEE_FIN - ISEE_INI);
                      if (pct < PCT_MIN) pct = PCT_MIN;
                      if (pct > PCT_MAX) pct = PCT_MAX;
                      label = pct.toFixed(2).replace(".", ",");
                      color = pct >= PCT_MAX ? "#c62828" : (pct <= PCT_MIN ? "#1b5e20" : "#0d47a1");
                      bg = pct >= PCT_MAX ? "#ffebee" : (pct <= PCT_MIN ? "#e8f5e9" : "#e3f2fd");
                    }
                  }
                  return <td style={{ ...tdN, fontWeight: 800, color, background: bg }}>{label}</td>;
                })()}
                <td style={tdN}>{fmt(r.isc_listino)}</td>
                <td style={tdN}>{fmt(r.isc_accord)}</td>
                <td style={{ ...tdHi, fontWeight: 800 }}>{fmt(r.isc_agev)}</td>
                <td style={tdN}>{r.nr_rette_listino || 0}</td>
                <td style={tdN}>{fmt(r.val_retta_listino)}</td>
                <td style={tdN}>{fmt(r.tot_rette_listino)}</td>
                <td style={tdN}>{r.nr_rette_accord || 0}</td>
                <td style={tdN}>{fmt(r.val_retta_accord)}</td>
                <td style={tdN}>{fmt(r.tot_rette_accord)}</td>
                <td style={{ ...tdHi, fontWeight: 800 }}>{fmt(r.agev_rette)}</td>
                <td style={{ ...tdN, background: "#e0f7fa", color: "#006064", fontWeight: 700 }}>{r.mensa_gg || 0}</td>
                <td style={tdN}>{fmt(r.mensa_agevolato)}</td>
                <td style={{ ...tdHi, fontWeight: 800 }}>{fmt(r.agev_mensa)}</td>
                <td style={{ ...tdHi, fontWeight: 800 }}>{fmt(r.agev_totale)}</td>
                <td style={{ ...tdN, fontWeight: 700 }}>{fmt(r.dote_scuola)}</td>
                <td style={{ ...tdHiStrong, fontWeight: 900 }}>{fmt(r.agev_netta)}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr style={{ background: "#e0e0e0", color: "black", fontWeight: 800, position: "sticky", bottom: 0 }}>
                {/* 1-6: checkbox + Cod + Cognome + Nome + Settore + Classe */}
                <td colSpan={6} style={{ textAlign: "right", padding: "4px 6px", borderTop: "2px solid #757575" }}>TOTALI</td>
                {/* 7: Stato */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 8: ISEE */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 9: %Contrib */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 10: Isc.Listino */}
                <td style={tdNT}>{fmt(tot.isc_listino)}</td>
                {/* 11: Isc.Accord */}
                <td style={tdNT}>{fmt(tot.isc_accord)}</td>
                {/* 12: Isc.Agev ⭐ */}
                <td style={tdNTHi}>{fmt(tot.isc_agev)}</td>
                {/* 13: NrR.L */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 14: ValR.L */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 15: TotR.L */}
                <td style={tdNT}>{fmt(tot.tot_rette_listino)}</td>
                {/* 16: NrR.A */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 17: ValR.A */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 18: TotR.A */}
                <td style={tdNT}>{fmt(tot.tot_rette_accord)}</td>
                {/* 19: Agev Retta ⭐ */}
                <td style={tdNTHi}>{fmt(tot.agev_rette)}</td>
                {/* 20: GG */}
                <td style={{ ...tdNT, background: "#b2ebf2", color: "#006064" }}>{tot.mensa_gg || 0}</td>
                {/* 21: MensaA */}
                <td style={{ ...tdNT, background: "#cfd8dc" }}>&nbsp;</td>
                {/* 22: Agev Mensa ⭐ */}
                <td style={tdNTHi}>{fmt(tot.agev_mensa)}</td>
                {/* 23: Agev Totale ⭐ */}
                <td style={tdNTHi}>{fmt(tot.agev_totale)}</td>
                {/* 24: Dote */}
                <td style={tdNT}>{fmt(tot.dote_scuola)}</td>
                {/* 25: Agev Netta ⭐⭐ */}
                <td style={tdNTHiStrong}>{fmt(tot.agev_netta)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}

      {/* 🆕 Tab COMPARATORE — confronta iscrizione/rette listino vs agevolate vs % contribuzione */}
      {tab === "comparatore" && (
        <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", maxHeight: "75vh", overflow: "auto" }}>
          <table className="sw-table" style={{ fontSize: 11, width: "100%" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ ...th, textAlign: "left", background: "#0277bd", color: "white" }}>Alunno</th>
                <th style={{ ...th, background: "#0277bd", color: "white" }}>Sett.</th>
                <th style={{ ...th, background: "#0277bd", color: "white" }}>Classe</th>
                <th style={{ ...th, background: "#01579b", color: "white", textAlign: "right" }}>ISEE</th>
                <th style={{ ...th, background: "#01579b", color: "white" }} title="% contribuzione = 34 + ISEE × (100−34) / (32.000−0), clamp [34%, 100%]">% Contrib.</th>
                <th style={{ ...th, background: "#2e7d32", color: "white", textAlign: "right" }}>Iscrizione LISTINO €</th>
                <th style={{ ...th, background: "#2e7d32", color: "white", textAlign: "right" }}>Iscrizione AGEVOLATA €</th>
                <th style={{ ...th, background: "#c62828", color: "white", textAlign: "right" }} title="Iscrizione listino × % contribuzione">Iscrizione su % €</th>
                <th style={{ ...th, background: "#2e7d32", color: "white", textAlign: "right" }}>Retta LISTINO × nr €</th>
                <th style={{ ...th, background: "#2e7d32", color: "white", textAlign: "right" }}>Retta AGEV. × nr €</th>
                <th style={{ ...th, background: "#c62828", color: "white", textAlign: "right" }} title="((nr_rate_listino × valore_listino × %contribuzione) + dote) / nr_rate_agevolate">Retta su % × nr €</th>
                <th style={{ ...th, background: "#6a1b9a", color: "white", textAlign: "center" }} title="Numero rate accordate dell'agevolazione">Nr Rate</th>
                <th style={{ ...th, background: "#6a1b9a", color: "white", textAlign: "right" }} title="Dote scuola (contributo Regione)">Dote €</th>
                <th style={{ ...th, background: "#6a1b9a", color: "white", textAlign: "right" }} title="Dote / nr rate accordate (contributo unitario per rata)">Dote / Rate €</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>
              ) : filteredComp.length === 0 ? (
                <tr><td colSpan={14} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna scheda</td></tr>
              ) : filteredComp.map((r, i) => {
                const iscL = Number(r.isc_listino) || 0;
                const iscA = Number(r.isc_accord) || 0;
                const valRl = Number(r.val_retta_listino) || 0;
                const nrRl = Number(r.nr_rette_listino) || 0;
                const nrRa = Number(r.nr_rette_accord) || 0;
                const dote = Number(r.dote_scuola) || 0;
                const totRl = Number(r.tot_rette_listino) || 0;
                const totRa = Number(r.tot_rette_accord) || 0;
                const pct = calcPctContrib(r.isee);
                const iscPct = pct != null ? (iscL * pct / 100) : iscL;
                // 🆕 Feb 2026 formula: ((nrRl × valRl × pct/100) + dote) / nrRa = € / rata
                const numerRettaPct = pct != null ? ((nrRl * valRl * pct / 100) + dote) : (nrRl * valRl + dote);
                const rettaPctTot = nrRa > 0 ? (numerRettaPct / nrRa) : 0;
                return (
                  <tr key={r.cod} data-testid={`comp-row-${r.cod}`} style={{ background: i % 2 ? "#fafafa" : "white" }}>
                    <td style={{ padding: "4px 6px", fontWeight: 700 }}>
                      {r.cognome} {r.nome}
                      <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>{r.cod}</div>
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>{r.settore || "—"}</td>
                    <td style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>{r.classe || "—"}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: r.isee != null ? "#0d47a1" : "#bbb" }}>
                      {r.isee != null ? fmt(r.isee) : "—"}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "center", fontFamily: "monospace", fontWeight: 800, color: pct == null ? "#bbb" : (pct >= 100 ? "#c62828" : (pct <= 34 ? "#1b5e20" : "#0d47a1")), background: pct == null ? "#fafafa" : (pct >= 100 ? "#ffebee" : (pct <= 34 ? "#e8f5e9" : "#e3f2fd")) }}>
                      {pct != null ? `${pct.toFixed(2).replace(".", ",")} %` : "—"}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace" }}>{fmt(iscL)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", background: "#e8f5e9" }}>{fmt(iscA)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", background: "#ffebee", fontWeight: 700 }}>{fmt(iscPct)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace" }}>{fmt(totRl)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", background: "#e8f5e9" }}>{fmt(totRa)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", background: "#ffebee", fontWeight: 700 }}>{fmt(rettaPctTot)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "center", fontFamily: "monospace", background: "#f3e5f5", fontWeight: 800, color: "#4a148c" }}>
                      {r.nr_rette_accord || 0}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", background: "#f3e5f5", fontWeight: 700, color: "#4a148c" }}>
                      {fmt(Number(r.dote_scuola) || 0)}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace", background: "#f3e5f5", fontWeight: 700, color: "#4a148c" }}
                      title={`Dote €${fmt(Number(r.dote_scuola) || 0)} / ${r.nr_rette_accord || 0} rate accordate`}>
                      {(() => {
                        const d = Number(r.dote_scuola) || 0;
                        const nr = Number(r.nr_rette_accord) || 0;
                        if (nr === 0) return "—";
                        return fmt(d / nr);
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: "#37474f", color: "white", fontWeight: 900, position: "sticky", bottom: 0 }}>
                  <td colSpan={3} style={{ padding: "8px 10px", textAlign: "right", fontSize: 12 }}>TOTALI ({filtered.length} alunni)</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontSize: 12, background: "#455a64" }}
                    title={`MEDIA ISEE su ${totComp.isee_count} alunni con ISEE valorizzato`}>
                    <div style={{ fontSize: 9, opacity: 0.7, fontWeight: 400 }}>media</div>
                    {fmt(totComp.isee_media)}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "center", fontSize: 11, background: "#455a64", fontStyle: "italic", opacity: 0.7 }}
                    title="Percentuale media pesata non calcolata (varia per riga)">—</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>{fmt(totComp.isc_listino)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", background: "#1b5e20", fontSize: 13 }}>{fmt(totComp.isc_agev)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", background: "#c62828", fontSize: 13 }}>{fmt(totComp.isc_pct)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>{fmt(totComp.retta_list_tot)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", background: "#1b5e20", fontSize: 13 }}>{fmt(totComp.retta_agev_tot)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", background: "#c62828", fontSize: 13 }}>{fmt(totComp.retta_pct_tot)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "center", fontSize: 11, background: "#4a148c", fontStyle: "italic", opacity: 0.7 }}
                    title="Somma rate accordate (non ha senso — varia per riga)">—</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", background: "#6a1b9a", fontSize: 13 }}>{fmt(totComp.dote)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "center", fontSize: 11, background: "#4a148c", fontStyle: "italic", opacity: 0.7 }}
                    title="Rapporto dote/rate varia per riga (non sommabile)">—</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

const th = { background: "#e0e0e0", color: "black", padding: "3px 4px", fontWeight: 800, fontSize: 9, textAlign: "center", borderBottom: "1px solid #999", borderRight: "1px solid #c8c8c8", whiteSpace: "nowrap", textTransform: "none" };
// 🆕 Feb 2026: header di colonne "AGEV" evidenziate (giallo tenue)
const thHi = { ...th, background: "#fff59d", color: "#7f6000", borderBottom: "2px solid #f9a825" };
// 🆕 Feb 2026: header ultima colonna AGEV NETTA (verde forte)
const thHiStrong = { ...th, background: "#a5d6a7", color: "#1b5e20", borderBottom: "2px solid #1b5e20" };
const tdN = { textAlign: "right", fontFamily: "Consolas, monospace", padding: "2px 4px", fontSize: 9 };
const tdHi = { ...tdN, background: "#fffde7", color: "#5d4037" };
const tdHiStrong = { ...tdN, background: "#c8e6c9", color: "#1b5e20" };
const tdNT = { textAlign: "right", fontFamily: "Consolas, monospace", padding: "4px 6px", background: "#bdbdbd", color: "black", borderTop: "2px solid #757575" };
const tdNTHi = { ...tdNT, background: "#fff176", color: "#7f6000", borderTop: "2px solid #f9a825", fontWeight: 900 };
const tdNTHiStrong = { ...tdNT, background: "#66bb6a", color: "white", borderTop: "2px solid #1b5e20", fontWeight: 900 };
